import { LoadedInfo, LoadInIframeResult } from '.';
import { IframeLoadOptions } from './loadComponentInIframe';
import { CreateFromCodeProps, RehydrateFromSnapshotProps } from './createComponentInDiv';
import { loadComponentInIframeInternal } from './Iframe/Outside/loadComponentInIframeInternal';
import { NamespaceLoggerAdapter } from '@fluidx/utilities';
import { InitializeConfig } from './Iframe-shared/InitializeConfig';

export interface CreateFromCodeInIframeProps extends CreateFromCodeProps {
  componentDiv: HTMLElement;
}

export interface RehydrateFromSnapshotInIframeProps extends RehydrateFromSnapshotProps {
  componentDiv: HTMLElement;
}

export interface CreateInIframeResult extends LoadInIframeResult {
  loadedInfo: Promise<Omit<LoadedInfo, 'realize'>>;
}

/**
 * [Experimental]
 * Creates a detached container and loads its default component the same way loadComponentInIframe does
 */
export function createComponentFromCodeInIframe(
  props: CreateFromCodeInIframeProps,
  options: IframeLoadOptions = {}
): CreateInIframeResult {
  return createOrRehydrateHelper(
    props,
    options,
    {
      type: 'CreateFromCode',
      driverType: props.driverType,
      codeDetails: props.codeDetails,
      heroComponentConfiguration: props.heroComponentConfiguration
    },
    Date.now() /* bootstrapperStartTime */
  );
}

/**
 * [Experimental]
 * Rehydrates a detached container from snapshot
 */
export function rehydrateComponentFromSnapshotInIframe(
  props: RehydrateFromSnapshotInIframeProps,
  options: IframeLoadOptions = {}
): CreateInIframeResult {
  return createOrRehydrateHelper(
    props,
    options,
    { type: 'RehydrateFromSnapshot', snapshot: props.snapshot, driverType: props.driverType },
    Date.now() /* bootstrapperStartTime */
  );
}

function createOrRehydrateHelper(
  props: CreateFromCodeInIframeProps | RehydrateFromSnapshotInIframeProps,
  options: IframeLoadOptions,
  initializeConfig: InitializeConfig,
  bootstrapperStartTime: number
) {
  const { codeLoader, logger: hostLogger, environment, ...partialConfig } = props.config || {};
  const logger =
    hostLogger &&
    new NamespaceLoggerAdapter('OfficeWebHost', hostLogger, {
      inIframe: true
    });

  // `HostConfig.environment` is a new concept, introduced in April 2021
  // (after `IframeLoadOptions.containerEnvironment` had already existed for 6+ months).
  // Thus, if `HostConfig.environment` is missing, we set it to match `options.containerEnvironment`
  // or to fall back to the iframe environment as a reasonable default.
  const newConfig = {
    ...partialConfig,
    environment: environment || options.containerEnvironment || options.iframeEnvironment
  };
  // TODO: in the next OWH version bump, remove `options.containerEnvironment`
  // When doing this work, just remove the `options.containerEnvironment ||` from above and tweak the comment above it.
  // Please search for other instances of this TODO text throughout the rest of the OWH project as well.

  return loadComponentInIframeInternal(
    initializeConfig,
    props.scope.TokenProvider,
    props.scope,
    props.componentDiv,
    newConfig,
    logger,
    bootstrapperStartTime,
    options
  );
}
